import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronRight, Copy, Link2, Plus } from 'lucide-react';
import { api, ApiError, qk } from '@client/lib/api';
import { useAdminItems } from '@client/lib/admin-api';
import { ViewerPageTimes, type PageTime } from '@client/components/admin/page-times';
import { Badge } from '@client/components/ui/badge';
import { Button } from '@client/components/ui/button';
import { EmptyState, Spinner } from '@client/components/ui/feedback';
import { Field, Input, Select } from '@client/components/ui/input';
import { ConfirmDialog, Dialog } from '@client/components/ui/overlay';
import { Table, Td, Th, Tr } from '@client/components/ui/table';
import { useToast } from '@client/components/ui/toast';
import { fmtDate, fmtDateTime, fmtDuration, fmtRelative } from '@client/lib/format';
import { cn } from '@client/lib/utils';
import { pickLocale, type Localised } from '@shared/types';

export const Route = createFileRoute('/admin/rooms/$id/share')({ component: ShareLinksTab });

interface ShareLinkRow {
  id: string; slug: string; label: string; url: string; item_id: string; item_title: Localised | null; access_days: number; allow_download: boolean; expires_at: string | null; disabled_at: string | null; created_at: string;
  blocker: 'disabled' | 'expired' | 'room_not_open' | 'item_missing' | 'item_not_published' | 'not_rendered' | null;
  funnel: { gate_views: number; requested: number; confirmed: number; active: number };
}
interface VisitorSession { id: string; started_at: string; ended_at: string; active_ms: number; country: string | null; device: string; path: Array<{ page_no: number; ms: number }> }
interface Activity {
  opens: number; total_ms: number; other_versions_ms: number; pages_seen: number; completion: number | null; first_open_at: string | null; last_seen_at: string | null; last_page: number | null;
  page_times: PageTime[]; sessions: VisitorSession[];
  signals: { downloads: number; downloads_blocked: number; print_attempts: number; copy_attempts: number; focus_lost: number; idle: number; thumbnail_jumps: number; searches: string[] };
}
interface Visitor { id: string; name: string; email: string; investor_id: string | null; status: 'pending' | 'active' | 'expired' | 'revoked'; requested_at: string; confirmed_at: string | null; access_expires_at: string | null; request_count: number; activity: Activity }
interface VisitorsResponse { version: { id: string; version_no: number; page_count: number | null } | null; visitors: Visitor[] }

const BLOCKERS: Record<NonNullable<ShareLinkRow['blocker']>, string> = {
  disabled: 'Switched off', expired: 'Expired', room_not_open: 'Room is not open', item_missing: 'Document deleted', item_not_published: 'Document not published', not_rendered: 'Document not ready',
};

function ShareLinksTab() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const key = qk.admin('share-links', id);
  const links = useQuery({ queryKey: key, queryFn: () => api<{ links: ShareLinkRow[] }>(`/api/admin/share-links?room_id=${id}`) });
  const [createOpen, setCreateOpen] = React.useState(false);
  const [openLink, setOpenLink] = React.useState<string | null>(null);
  const toggle = useMutation({
    mutationFn: (l: ShareLinkRow) => api(`/api/admin/share-links/${l.id}`, { method: 'PATCH', body: { disabled: !l.disabled_at } }),
    onSuccess: (_r, l) => { qc.invalidateQueries({ queryKey: key }); toast.push({ tone: 'good', title: l.disabled_at ? 'Link switched on' : 'Link switched off · nobody can open the document through it' }); },
    onError: (e) => toast.push({ tone: 'critical', title: 'Could not change the link', body: e instanceof ApiError ? e.code : String(e) }),
  });
  if (links.isPending) return <Spinner />;
  const list = links.data?.links ?? [];
  return (
    <div className="space-y-4 px-6 py-6 md:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="section-title">Share links</h2>
          <p className="mt-1 max-w-2xl text-[13px] text-muted">Send one fixed link to one document. Visitors give their name and email, confirm by email, and see only that document. Every page they read is measured here.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}><Plus />New share link</Button>
      </div>
      {list.length === 0 ? <EmptyState title="No share links yet" body="Create one for a published document, such as your pitch deck." /> : list.map((l) => (
        <LinkCard key={l.id} link={l} open={openLink === l.id} onToggleOpen={() => setOpenLink(openLink === l.id ? null : l.id)} onToggleEnabled={() => toggle.mutate(l)} />
      ))}
      {createOpen ? <CreateLinkDialog roomId={id} onClose={() => setCreateOpen(false)} onCreated={(linkId) => { setCreateOpen(false); setOpenLink(linkId); qc.invalidateQueries({ queryKey: key }); }} /> : null}
    </div>
  );
}

function LinkCard({ link, open, onToggleOpen, onToggleEnabled }: { link: ShareLinkRow; open: boolean; onToggleOpen: () => void; onToggleEnabled: () => void }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(link.url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  const f = link.funnel;
  return (
    <section className="rounded-card border border-border bg-card">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 p-4">
        <button type="button" aria-expanded={open} onClick={onToggleOpen} className="flex min-w-0 flex-1 basis-64 items-center gap-2 text-left">
          <ChevronRight className={cn('size-4 shrink-0 text-muted transition-transform duration-150', open && 'rotate-90')} aria-hidden />
          <span className="min-w-0">
            <span className="block truncate text-[14px] font-[600]">{link.label}</span>
            <span className="block truncate text-[12.5px] text-muted">{link.item_title ? pickLocale(link.item_title, 'en') : 'Deleted document'} · {link.access_days} {link.access_days === 1 ? 'day' : 'days'} after confirming{link.allow_download ? ' · downloads on' : ''}{link.expires_at ? ` · link ends ${fmtDate(link.expires_at, 'en')}` : ''}</span>
          </span>
        </button>
        {link.blocker ? <Badge tone="warning">{BLOCKERS[link.blocker]}</Badge> : <Badge tone="good">Live</Badge>}
        <p className="text-[13px] tabular-nums text-muted">{f.gate_views} {f.gate_views === 1 ? 'view' : 'views'} · {f.requested} asked · {f.confirmed} confirmed</p>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="secondary" onClick={() => void copy()}>{copied ? <Check /> : <Copy />}{copied ? 'Copied' : 'Copy link'}</Button>
          <Button size="sm" variant="ghost" onClick={onToggleEnabled}>{link.disabled_at ? 'Switch on' : 'Switch off'}</Button>
        </div>
      </div>
      {open ? <div className="border-t border-border"><p className="flex items-center gap-1.5 break-all px-4 py-2 text-[12.5px] text-muted"><Link2 className="size-3.5 shrink-0" aria-hidden />{link.url}</p><VisitorsTable linkId={link.id} /></div> : null}
    </section>
  );
}

const STATUS: Record<Visitor['status'], { label: string; tone: 'good' | 'warning' | 'neutral' | 'critical' }> = {
  pending: { label: 'Not confirmed', tone: 'warning' }, active: { label: 'Active', tone: 'good' }, expired: { label: 'Expired', tone: 'neutral' }, revoked: { label: 'Revoked', tone: 'critical' },
};

function VisitorsTable({ linkId }: { linkId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = qk.admin('share-visitors', linkId);
  const data = useQuery({ queryKey: key, queryFn: () => api<VisitorsResponse>(`/api/admin/share-links/${linkId}/visitors`) });
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [revoking, setRevoking] = React.useState<Visitor | null>(null);
  const change = useMutation({
    mutationFn: ({ v, action }: { v: Visitor; action: 'revoke' | 'restore' }) => api(`/api/admin/share-links/visitors/${v.id}/${action}`, { method: 'POST' }),
    onSuccess: (_r, { action }) => { setRevoking(null); qc.invalidateQueries({ queryKey: key }); toast.push({ tone: 'good', title: action === 'revoke' ? 'Access revoked · effective on their next request' : 'Access restored · they confirm their email again' }); },
    onError: (e) => toast.push({ tone: 'critical', title: 'Could not change access', body: e instanceof ApiError ? e.code : String(e) }),
  });
  if (data.isPending) return <div className="p-4"><Spinner /></div>;
  const visitors = data.data?.visitors ?? [];
  if (visitors.length === 0) return <p className="px-4 pb-4 text-[13px] text-muted">Nobody has used this link yet.</p>;
  return (
    <>
      <Table>
        <thead><tr><Th>Visitor</Th><Th>Status</Th><Th align="right">Time</Th><Th align="right">Pages</Th><Th align="right">Opens</Th><Th>Last seen</Th><Th><span className="sr-only">Actions</span></Th></tr></thead>
        <tbody>
          {visitors.map((v) => {
            const open = openId === v.id;
            const a = v.activity;
            return (
              <React.Fragment key={v.id}>
                <Tr clickable onClick={() => setOpenId(open ? null : v.id)}>
                  <Td><button type="button" aria-expanded={open} className="inline-flex items-center gap-1.5 text-left font-[520]"><ChevronRight className={cn('size-3.5 shrink-0 text-muted transition-transform duration-150', open && 'rotate-90')} aria-hidden />{v.name}</button><span className="ml-2 text-muted">{v.email}</span></Td>
                  <Td><Badge tone={STATUS[v.status].tone}>{STATUS[v.status].label}</Badge></Td>
                  <Td align="right">{a.total_ms ? fmtDuration(a.total_ms) : '—'}</Td>
                  <Td align="right">{data.data?.version?.page_count ? `${a.pages_seen} / ${data.data.version.page_count}` : a.pages_seen}</Td>
                  <Td align="right">{a.opens}</Td>
                  <Td className="whitespace-nowrap text-muted">{a.last_seen_at ? fmtRelative(a.last_seen_at, 'en') : '—'}</Td>
                  <Td className="whitespace-nowrap text-right">
                    {v.status === 'revoked'
                      ? <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); change.mutate({ v, action: 'restore' }); }}>Restore</Button>
                      : <Button size="sm" variant="ghost" className="text-critical" onClick={(e) => { e.stopPropagation(); setRevoking(v); }}>Revoke</Button>}
                  </Td>
                </Tr>
                {open ? <tr><td colSpan={7} className="border-b border-border bg-soft px-4 py-3"><VisitorDetail visitor={v} pageCount={data.data?.version?.page_count ?? 0} versionNo={data.data?.version?.version_no ?? null} /></td></tr> : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
      <ConfirmDialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)} title={`Revoke ${revoking?.name ?? 'this person'}?`} description="They lose this document on their next request, and asking through the link again does nothing." confirmLabel="Revoke" destructive loading={change.isPending} onConfirm={() => revoking && change.mutate({ v: revoking, action: 'revoke' })} />
    </>
  );
}

function VisitorDetail({ visitor, pageCount, versionNo }: { visitor: Visitor; pageCount: number; versionNo: number | null }) {
  const a = visitor.activity;
  const s = a.signals;
  const signals = [
    ['Tab switches', s.focus_lost], ['Went idle', s.idle], ['Thumbnail jumps', s.thumbnail_jumps], ['Downloads', s.downloads], ['Blocked downloads', s.downloads_blocked], ['Print attempts', s.print_attempts], ['Copy attempts', s.copy_attempts],
  ] as const;
  return (
    <div className="space-y-4">
      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-[12.5px]">
        <Fact label="Asked" value={fmtDateTime(visitor.requested_at, 'en')} />
        <Fact label="Confirmed" value={visitor.confirmed_at ? fmtDateTime(visitor.confirmed_at, 'en') : 'Not yet'} />
        {visitor.access_expires_at ? <Fact label="Access until" value={fmtDateTime(visitor.access_expires_at, 'en')} /> : null}
        {a.first_open_at ? <Fact label="First opened" value={fmtDateTime(a.first_open_at, 'en')} /> : null}
        {a.completion != null ? <Fact label="Completion" value={`${Math.round(a.completion * 100)}%`} /> : null}
        {a.last_page ? <Fact label="Last page" value={String(a.last_page)} /> : null}
        {a.other_versions_ms ? <Fact label="On earlier versions" value={fmtDuration(a.other_versions_ms)} /> : null}
      </dl>
      <ViewerPageTimes pageCount={pageCount} versionNo={versionNo} times={a.page_times} />
      {a.sessions.length > 0 ? (
        <div>
          <h3 className="eyebrow mb-2">Visits</h3>
          <ul className="space-y-2">
            {a.sessions.map((session) => (
              <li key={session.id} className="text-[12.5px]">
                <p className="font-[560]">{fmtDateTime(session.started_at, 'en')} · {fmtDuration(session.active_ms)} · {session.device}{session.country ? ` · ${session.country}` : ''}</p>
                <p className="mt-0.5 text-muted tabular-nums">{session.path.map((p) => `p.${p.page_no} ${fmtDuration(p.ms)}`).join(' → ')}</p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {a.opens > 0 ? (
        <div>
          <h3 className="eyebrow mb-2">Signals</h3>
          <dl className="flex flex-wrap gap-x-6 gap-y-1 text-[12.5px]">{signals.map(([label, n]) => <Fact key={label} label={label} value={String(n)} />)}</dl>
          {s.searches.length > 0 ? <p className="mt-2 text-[12.5px]"><span className="text-muted">Searched for: </span>{s.searches.join(', ')}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div className="flex gap-1.5"><dt className="text-muted">{label}</dt><dd className="font-[540] tabular-nums">{value}</dd></div>;
}

function CreateLinkDialog({ roomId, onClose, onCreated }: { roomId: string; onClose: () => void; onCreated: (id: string) => void }) {
  const toast = useToast();
  const items = useAdminItems(roomId);
  const docs = (items.data?.items ?? []).filter((i) => i.type === 'document' && !i.deletedAt);
  const [itemId, setItemId] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [days, setDays] = React.useState('7');
  const [endsOn, setEndsOn] = React.useState('');
  const [download, setDownload] = React.useState(false);
  const selected = itemId || docs[0]?.id || '';
  const accessDays = Number.parseInt(days, 10);
  const valid = !!selected && !!label.trim() && accessDays >= 1 && accessDays <= 365;
  const create = useMutation({
    mutationFn: () => api<{ link: { id: string } }>('/api/admin/share-links', { body: { item_id: selected, label: label.trim(), access_days: accessDays, allow_download: download, expires_at: endsOn ? new Date(`${endsOn}T23:59:59Z`).toISOString() : null } }),
    onSuccess: (r) => { toast.push({ tone: 'good', title: 'Share link created' }); onCreated(r.link.id); },
    onError: (e) => toast.push({ tone: 'critical', title: 'Could not create the link', body: e instanceof ApiError ? e.code : String(e) }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="New share link" description="Skips the room's confidentiality agreement and any item restrictions: anyone with the link can get this one document by confirming an email." footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => create.mutate()} disabled={!valid} loading={create.isPending}>Create link</Button></>}>
      {docs.length === 0 ? <p className="text-[13px] text-muted">This room has no documents yet. Upload a PDF under Content first.</p> : (
        <>
          <Field label="Document" htmlFor="share-doc"><Select id="share-doc" value={selected} onChange={(e) => setItemId(e.target.value)}>{docs.map((d) => <option key={d.id} value={d.id}>{pickLocale(d.title, 'en')}{d.status !== 'published' ? ' (not published)' : ''}</option>)}</Select></Field>
          <Field label="Name (only you see this)" htmlFor="share-label"><Input id="share-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Pitch deck, November outreach" /></Field>
          <Field label="Days open after confirming the email" htmlFor="share-days"><Input id="share-days" type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} /></Field>
          <Field label="Close the link on (optional)" htmlFor="share-ends"><Input id="share-ends" type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} /></Field>
          <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" className="accent-brand" checked={download} onChange={(e) => setDownload(e.target.checked)} />Allow downloading the PDF</label>
        </>
      )}
    </Dialog>
  );
}
